'use client';

import { Grid2, Box, Typography, Container } from '@mui/material';
import { useIsMobile } from '@qb/frontend/hooks/mediaQueries';
import { BenefitsBanner } from '@/global/pages/PartDisplayPage/_components/BenefitsBanner';
import { CompareProductsCarousel } from '@/global/pages/PartDisplayPage/_components/CompareProductsCarousel/CompareProductsCarousel';
import { PartAboutTheCategory } from '@/global/pages/PartDisplayPage/_components/PartAboutTheCategory';
import { PartAboutTheManufacturer } from '@/global/pages/PartDisplayPage/_components/PartAboutTheManufacturer';
import { PartDisplayPageFaq } from '@/global/pages/PartDisplayPage/_components/PartDisplayPageFAQ';
import { PartPhotoGallery } from '@/global/pages/PartDisplayPage/_components/PartPhotoGallery';
import { RecentlyViewedProductsCarousel } from '@/global/pages/PartDisplayPage/_components/RecentlyViewedProductsCarousel/RecentlyViewedProductsCarousel';
import { SimilarProductsFromSameCategoryCarousel } from '@/global/pages/PartDisplayPage/_components/SimilarProductsFromSameCategoryCarousel';
import { useRecordPartView } from '@/global/pages/PartDisplayPage/_hooks/useRecordPartView';
import { PartDisplayParams } from '@/shared/classes/SPSPageComponentParams/PartDisplayParams';
import { TenantConfig } from '@/shared/types/controllers/sps/SPSAppControllerTypes';
import { PartDisplayDataResponse, PartRelevantPartsDataResponse, PartSameManufacturerPartCategoryPartsResponse, PartGetSamePartCategoryPartsResponse, PartGetRecentlyViewedParts, PartGetComparePartsResponse } from '@/shared/types/controllers/sps/SPSPartControllerTypes';
import { PartBasicInfo } from './_components/PartBasicInfo';
import { PartBreadcrumbs } from './_components/PartBreadcrumbs';
import { PartCondition } from './_components/PartCondition';
import { PartDivider } from './_components/PartDivider';
import { MemoizedPartFeatures, getHasProductDetails } from './_components/PartFeatures';
import { PartLongDescription } from './_components/PartLongDescription';
import { PartRelatedDocuments } from './_components/PartRelatedDocuments';
import { PartRelevantPartsCarousel } from './_components/PartRelevantPartsCarousel';
import { SimilarPartsFromSameCategorySameManufacturer } from './_components/SimilarPartsFromSameCategorySameManufacturer/SimilarPartsFromSameCategorySameManufacturer';
import { TrustBox } from './_components/TrustBox/TrustBox';
type PartDisplayPageProps = PartDisplayDataResponse & {
  canonicalUrl: string;
  relevantParts: PartRelevantPartsDataResponse['parts'];
  samePartCategoryParts: PartGetSamePartCategoryPartsResponse['parts'];
  sameManufacturerPartCategoryParts: PartSameManufacturerPartCategoryPartsResponse['parts'];
  recentlyViewedParts: PartGetRecentlyViewedParts['parts'];
  compareParts: PartGetComparePartsResponse['parts'];
  partDisplayParams: PartDisplayParams;
  tenantConfig: TenantConfig;
};
export const PartDisplayPage = ({
  part,
  manufacturerDescription,
  partCategoryDescription,
  partCategoriesToRoot,
  relevantParts,
  sameManufacturerPartCategoryParts,
  samePartCategoryParts,
  recentlyViewedParts,
  compareParts,
  canonicalUrl,
  basicParametricDatas,
  featureInstances,
  documents,
  publicQtyAndPrice,
  partDisplayParams,
  tenantConfig,
  spPartPageSetting
}: PartDisplayPageProps) => {
  const isMobile = useIsMobile();
  const partCategory = partCategoriesToRoot.at(-1);
  const hasProductDetails = getHasProductDetails(featureInstances, basicParametricDatas);
  useRecordPartView(part.id);
  return <>
      <Container sx={{
      pt: {
        xs: 1.5,
        md: 3,
        minHeight: 0
      }
    }} data-sentry-element="Container" data-sentry-source-file="PartDisplayPage.tsx">
        <Grid2 container columnSpacing={[0, 0, 3]} data-sentry-element="Grid2" data-sentry-source-file="PartDisplayPage.tsx">
          <Grid2 component="section" size={{
          xs: 12,
          md: 8
        }} data-sentry-element="Grid2" data-sentry-source-file="PartDisplayPage.tsx">
            <Box sx={{
            pb: {
              xs: 1.5,
              md: 3
            },
            pt: {
              xs: 1,
              md: 0
            }
          }} data-sentry-element="Box" data-sentry-source-file="PartDisplayPage.tsx">
              <PartBreadcrumbs partID={part.id} partNumber={part.partNumber} partManufacturerName={part.manufacturer.name} partCategoriesToRoot={partCategoriesToRoot} data-sentry-element="PartBreadcrumbs" data-sentry-source-file="PartDisplayPage.tsx" />
            </Box>
            <Grid2 container columns={8} spacing={3} size={{
            xs: 12
          }} sx={{
            mb: 1
          }} data-sentry-element="Grid2" data-sentry-source-file="PartDisplayPage.tsx">
              <Grid2 size={{
              xs: 8
            }} sx={{
              py: 1
            }} data-sentry-element="Grid2" data-sentry-source-file="PartDisplayPage.tsx">
                <Typography variant="h2" component="h1" sx={{
                fontSize: [26, 36],
                lineHeight: 1.3,
                mt: [1, 1, 0]
              }} data-sentry-element="Typography" data-sentry-source-file="PartDisplayPage.tsx">
                  {part.partNumber} {part.manufacturer.name}{' '}
                  {partCategory?.name}
                </Typography>
              </Grid2>
              <Grid2 size={{
              xs: 8,
              md: 3
            }} data-sentry-element="Grid2" data-sentry-source-file="PartDisplayPage.tsx">
                <PartPhotoGallery part={part} images={part.images} tenantConfig={tenantConfig} data-sentry-element="PartPhotoGallery" data-sentry-source-file="PartDisplayPage.tsx" />
              </Grid2>
              <Grid2 size={{
              xs: 8,
              md: 5
            }} sx={{
              pt: [0, 0, 1.5],
              display: 'flex',
              flexDirection: 'column'
            }} data-sentry-element="Grid2" data-sentry-source-file="PartDisplayPage.tsx">
                <PartBasicInfo tenantConfig={tenantConfig} partCategoriesToRoot={partCategoriesToRoot} part={part} publicQtyAndPrice={publicQtyAndPrice} partDisplayParams={partDisplayParams} spPartPageSetting={spPartPageSetting} data-sentry-element="PartBasicInfo" data-sentry-source-file="PartDisplayPage.tsx" />
              </Grid2>
            </Grid2>
            {hasProductDetails && <>
                <MemoizedPartFeatures featureInstances={featureInstances} basicParametricDatas={basicParametricDatas} partAbsoluteUrl={canonicalUrl} />
                <PartDivider />
              </>}
            <PartCondition price={publicQtyAndPrice.price} tenantCompanyName={tenantConfig.companyName} part={part} data-sentry-element="PartCondition" data-sentry-source-file="PartDisplayPage.tsx" />
            <PartDivider data-sentry-element="PartDivider" data-sentry-source-file="PartDisplayPage.tsx" />
            {part.longDescription?.length > 0 && <>
                <PartLongDescription shortDescription={part.description} longDescription={part.longDescription} />
                <PartDivider />
              </>}
            <PartRelatedDocuments part={part} documents={documents} data-sentry-element="PartRelatedDocuments" data-sentry-source-file="PartDisplayPage.tsx" />
            <SimilarPartsFromSameCategorySameManufacturer tenantConfig={tenantConfig} parts={sameManufacturerPartCategoryParts} categoryName={partCategory?.name} manufacturerName={part?.manufacturer?.name} data-sentry-element="SimilarPartsFromSameCategorySameManufacturer" data-sentry-source-file="PartDisplayPage.tsx" />
          </Grid2>
          {!isMobile && <Grid2 data-cy="StickySidebar" component="section" size={{
          xs: 12,
          md: 4
        }}>
              <TrustBox tenantConfig={tenantConfig} partDisplayParams={partDisplayParams} part={part} publicQtyAndPrice={publicQtyAndPrice} spPartPageSetting={spPartPageSetting} />
            </Grid2>}
        </Grid2>
        <CompareProductsCarousel partCategoryName={partCategory?.name} selectedPart={{
        ...part,
        publicQtyAndPrice,
        featureInstances,
        basicParametricDatas
      }} partsToCompare={compareParts} tenantAlias={tenantConfig.tenantAlias} data-sentry-element="CompareProductsCarousel" data-sentry-source-file="PartDisplayPage.tsx" />
        <PartAboutTheManufacturer manufacturerDescription={manufacturerDescription?.manufacturerDescription} manufacturer={part.manufacturer} data-sentry-element="PartAboutTheManufacturer" data-sentry-source-file="PartDisplayPage.tsx" />
        {!!partCategory && <PartAboutTheCategory partCategoryDescription={partCategoryDescription?.partCategoryDescription} partCategory={partCategory} />}
      </Container>
      {recentlyViewedParts.length > 0 && <RecentlyViewedProductsCarousel tenantConfig={tenantConfig} parts={recentlyViewedParts} />}
      {relevantParts.length > 0 && <PartRelevantPartsCarousel tenantConfig={tenantConfig} parts={relevantParts} partAbsoluteUrl={canonicalUrl} />}
      <PartDisplayPageFaq partName={part.partNumber} partDescription={part.description} clientName={tenantConfig.companyName} stock={publicQtyAndPrice.onHandQty} price={publicQtyAndPrice.price} partDisplayParams={partDisplayParams} data-sentry-element="PartDisplayPageFaq" data-sentry-source-file="PartDisplayPage.tsx" />
      {!!partCategory && samePartCategoryParts.length > 0 && <SimilarProductsFromSameCategoryCarousel tenantConfig={tenantConfig} partCategory={partCategory} parts={samePartCategoryParts} />}
      <BenefitsBanner data-sentry-element="BenefitsBanner" data-sentry-source-file="PartDisplayPage.tsx" />
    </>;
};